import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, ThumbsUp, ThumbsDown, ExternalLink, Heart, ShoppingCart, Sparkles, Users, TrendingUp, Layers, Frown } from 'lucide-react';
import { getRecommendations, markRecommendationAsViewed, giveRecommendationFeedback, RecommendationOptions } from '../lib/recommendationService';
import { toggleOwned, toggleWishlist, getUserCollection } from '../lib/gameService';
import { useAuth } from '../contexts/AuthContext';
import type { Recommendation, RecommendationAlgorithm, UserGame, RecommendationRequest } from '../types/index';
import { supabase } from '../lib/supabase';
import { RecommendationCard } from '../components/RecommendationCard';
import { RecommendationConsistencyService } from '../lib/services/recommendationConsistencyService';
import SEO from '../components/SEO';

// Nova interface para organizar as recomendações
interface RecommendationsData {
  popular: Recommendation[];
  personalized: Recommendation[];
}

const RecommendationsPage: React.FC = () => {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState<RecommendationsData>({ popular: [], personalized: [] });
  const [userGames, setUserGames] = useState<Record<string, UserGame>>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstTimeUser, setFirstTimeUser] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [insufficientData, setInsufficientData] = useState<boolean>(false);
  const [onlyPopularRecommendations, setOnlyPopularRecommendations] = useState<boolean>(false);
  const [dataStats, setDataStats] = useState<{
    ratedGames: number;
    playedGames: number;
    wishlistGames: number;
    totalGames: number;
  }>({ ratedGames: 0, playedGames: 0, wishlistGames: 0, totalGames: 0 });

  useEffect(() => {
    if (user) {
      loadRecommendationsAndUserGames(false);
    }
  }, [user]);

  // Função auxiliar para buscar jogos que o usuário não gostou
  async function fetchDislikedGames(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', false);
        
      if (error) {
        return [];
      }
      
      return data?.map(item => item.game_id) || [];
    } catch (error) {
      return [];
    }
  }

  // Carregar recomendações e jogos do usuário
  const loadRecommendationsAndUserGames = async (forcePersonalized: boolean = false) => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      setOnlyPopularRecommendations(false);
      
      // NOVO: Verificar e corrigir inconsistências nas recomendações
      try {
        const consistencyService = new RecommendationConsistencyService();
        const result = await consistencyService.verifyAndFix(user.id);
        
        if (result.total > 0) {
        }
        
        // Limpar recomendações antigas sem feedback
        const cleanupCount = await consistencyService.cleanupOldRecommendations(user.id, 30);
        if (cleanupCount > 0) {
        }
      } catch (consistencyError) {
      }
      
      // Buscar jogos com downvote para garantir que não sejam exibidos
      const dislikedGames = await fetchDislikedGames(user.id);
      
      // Carregar coleção do usuário
      const collection = await getUserCollection(user.id);
      
      // Se o usuário não tem jogos, mostrar tela de primeiro acesso
      if (!collection || collection.length === 0) {
        setFirstTimeUser(true);
        setLoading(false);
        setInsufficientData(true);
        setDataStats({ ratedGames: 0, playedGames: 0, wishlistGames: 0, totalGames: 0 });
        return;
      }
      
      // Transformar em um objeto para acesso rápido por ID
      const gameMap: Record<string, UserGame> = {};
      collection.forEach(userGame => {
        gameMap[userGame.game_id] = userGame;
      });
      setUserGames(gameMap);
      
      // Verificar se o usuário tem dados suficientes para recomendações de qualidade
      const userGameValues = Object.values(gameMap);
      const totalGames = userGameValues.length;
      const ratedGamesCount = userGameValues.filter(game => game.rating !== null && game.rating !== undefined).length;
      const playedGamesCount = userGameValues.filter(game => game.play_count && game.play_count > 0).length;
      const wishlistGamesCount = userGameValues.filter(game => game.wishlist).length;
      
      setDataStats({
        ratedGames: ratedGamesCount,
        playedGames: playedGamesCount,
        wishlistGames: wishlistGamesCount,
        totalGames
      });
      
      // Verificar se o usuário tem dados suficientes (pelo menos 3 em qualquer categoria)
      const hasEnoughData = ratedGamesCount >= 3 || playedGamesCount >= 3 || wishlistGamesCount >= 3 || totalGames >= 3;
      setInsufficientData(!hasEnoughData);
      
      // Modificado: Adicionar objeto de configuração com forcePersonalized
      const requestOptions: RecommendationOptions = {
        forcePersonalized: forcePersonalized 
      };
      
      // Carregar recomendações com opção para forçar recomendações personalizadas
      console.log(`Buscando recomendações para o usuário ${user.id} com forcePersonalized=${forcePersonalized}`);
      const recommendationsData = await getRecommendations(user.id, requestOptions);
      
      // Log para debug dos dados retornados pela API
      console.log(`[DEBUG] Recomendações recebidas - Populares: ${recommendationsData.popular?.length || 0}, Personalizadas: ${recommendationsData.personalized?.length || 0}`);
      
      // Verificar se as recomendações são válidas (devem ter objeto game associado)
      const validatedPopular = recommendationsData.popular
        .filter(rec => rec && rec.game && rec.game.id)
        .slice(0, 3);
        
      const validatedPersonalized = recommendationsData.personalized
        .filter(rec => rec && rec.game && rec.game.id)
        .slice(0, 3);
      
      // Verificar se algum jogo já avaliado está nas recomendações
      const ratedGamesMap = new Map();
      Object.values(gameMap).forEach(userGame => {
        if (userGame.rating !== null && userGame.rating !== undefined) {
          ratedGamesMap.set(userGame.game_id, userGame);
        }
      });
      
      console.log(`[DEBUG] RecommendationsPage - O usuário já avaliou ${ratedGamesMap.size} jogos`);
      
      // Filtrar jogos já avaliados
      const finalPopular = validatedPopular.filter(rec => {
        const isRated = ratedGamesMap.has(rec.game_id);
        if (isRated) {
          console.warn(`[ALERTA] Jogo já avaliado encontrado nas recomendações populares: ${rec.game_id} (${rec.game?.name})`);
        }
        return !isRated;
      });
      
      const finalPersonalized = validatedPersonalized.filter(rec => {
        const isRated = ratedGamesMap.has(rec.game_id);
        if (isRated) {
          console.warn(`[ALERTA] Jogo já avaliado encontrado nas recomendações personalizadas: ${rec.game_id} (${rec.game?.name})`);
        }
        return !isRated;
      });
      
      // Se houve alguma remoção, registrar
      if (finalPopular.length < validatedPopular.length) {
        console.warn(`[ALERTA] Removidos ${validatedPopular.length - finalPopular.length} jogos já avaliados das recomendações populares`);
      }
      
      if (finalPersonalized.length < validatedPersonalized.length) {
        console.warn(`[ALERTA] Removidos ${validatedPersonalized.length - finalPersonalized.length} jogos já avaliados das recomendações personalizadas`);
      }
      
      // Preparar dados validados
      const validatedData = {
        popular: finalPopular as any[],  // Usar any para contornar problemas de tipagem
        personalized: finalPersonalized as any[]
      };
      
      // Atualizar o estado com as recomendações validadas
      setRecommendations(validatedData);
      
      // Se o usuário tem dados suficientes, mas só recebeu recomendações populares
      if (hasEnoughData && 
          validatedData.popular.length > 0 && 
          validatedData.personalized.length === 0) {
        console.log('[INFO] Usuário tem dados suficientes, mas só recebeu recomendações populares');
        setOnlyPopularRecommendations(true);
      } else {
        setOnlyPopularRecommendations(false);
      }
      
      // Se não houver recomendações válidas mas tiver jogos na coleção, 
      // mostrar tela de primeiro acesso para gerar recomendações
      const hasValidRecommendations = validatedData.popular.length > 0 || validatedData.personalized.length > 0;
      
      if (!hasValidRecommendations && collection.length > 0) {
        console.log('Sem recomendações válidas mas com jogos na coleção, mostrando opção para gerar');
        setFirstTimeUser(true);
      } else {
        // Caso tenha recomendações válidas, garantir que não estamos no modo de primeiro acesso
        setFirstTimeUser(false);
      }
      
      // Marcar recomendações como visualizadas
      const allRecommendations = [...validatedData.popular, ...validatedData.personalized];
      for (const rec of allRecommendations) {
        if (!rec.viewed) {
          try {
            // Adicionar um atraso para evitar muitas requisições simultâneas
            await new Promise(resolve => setTimeout(resolve, 100));
            // Usar catch diretamente na chamada para não interromper o loop
            await markRecommendationAsViewed(rec.id).catch(viewError => {
              console.debug(`Não foi possível marcar recomendação ${rec.id} como visualizada:`, viewError);
            });
          } catch (viewError) {
            // Apenas log em nível de debug, não é um erro crítico
            console.debug(`Não foi possível marcar recomendação ${rec.id} como visualizada`, viewError);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar recomendações:', error);
      setError('Não foi possível carregar suas recomendações. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!user?.id) return;
    
    try {
      setGenerating(true);
      setError(null);
      
      // Forçar a geração de recomendações personalizadas
      console.log('Gerando novas recomendações personalizadas com forcePersonalized=true...');
      console.log('Jogos com feedback anterior serão excluídos das novas recomendações');
      
      try {
        // Recarregar com forcePersonalized=true para tentar priorizar algoritmos personalizados
        await loadRecommendationsAndUserGames(true);
        
        // Exibir confirmação para o usuário
        setSuccess('Novas recomendações carregadas com sucesso!');
        console.log('Recomendações personalizadas geradas com sucesso.');
        
        // Limpar a mensagem de sucesso após alguns segundos
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
        
        // Remover o status de "primeiro acesso" se estiver ativo
        if (firstTimeUser) {
          setFirstTimeUser(false);
        }
      } catch (genError) {
        console.error('Erro ao gerar recomendações:', genError);
        throw genError;
      }
    } catch (error) {
      console.error('Erro ao gerar recomendações:', error);
      
      // Tentar fornecer mensagem de erro mais específica
      let errorMessage = 'Não foi possível gerar novas recomendações. ';
      
      if (error instanceof Error) {
        if (error.message.includes('not.in')) {
          errorMessage += 'Erro na consulta ao banco de dados. Tente novamente mais tarde.';
        } else if (error.message.includes('algorithm')) {
          errorMessage += 'Erro na configuração do sistema de recomendação. Um administrador precisa verificar o banco de dados.';
        } else if (error.message.includes('permission') || error.message.includes('autoriza')) {
          errorMessage += 'Você não tem permissão para realizar esta ação.';
        } else {
          errorMessage += 'Ocorreu um erro interno. Tente novamente mais tarde.';
        }
      } else {
        errorMessage += 'Ocorreu um erro inesperado. Tente novamente mais tarde.';
      }
      
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  // Função para carregar apenas os jogos do usuário (sem recarregar recomendações)
  const loadUserGames = async () => {
    if (!user?.id) return;
    
    try {
      // Carregar coleção do usuário
      const collection = await getUserCollection(user.id);
      console.log(`Coleção do usuário atualizada: ${collection.length} jogos`);

      // Transformar em um objeto para acesso rápido por ID
      const gameMap: Record<string, UserGame> = {};
      collection.forEach(userGame => {
        gameMap[userGame.game_id] = userGame;
      });
      setUserGames(gameMap);
    } catch (error) {
      console.error('Erro ao atualizar coleção do usuário:', error);
    }
  };

  // Converter para useCallback para evitar re-renderizações
  const handleFeedback = useCallback(async (recommendationId: string, liked: boolean, isPopular: boolean) => {
    // Encontrar a recomendação atual no estado local
    const recommendationsList = isPopular ? recommendations.popular : recommendations.personalized;
    const currentRecommendation = recommendationsList.find(r => r.id === recommendationId);
    
    if (!currentRecommendation) {
      console.error(`Recomendação com ID ${recommendationId} não encontrada no estado local para feedback`);
      return;
    }
    
    const previousLikedValue = currentRecommendation.liked;
    const isToggle = previousLikedValue === liked;
    const newLikedValue = isToggle ? null : liked;

    console.log(`Feedback: ID=${recommendationId}, Novo valor=${newLikedValue}`);

    // --- Atualização Otimista da UI ---
    const optimisticUpdatedList = recommendationsList.map(r => 
      r.id === recommendationId ? { ...r, liked: newLikedValue } : r
    );
    setRecommendations(prevState => ({
      ...prevState,
      [isPopular ? 'popular' : 'personalized']: optimisticUpdatedList
    }));
    // ---------------------------------

    try {
      // Chamar o serviço para persistir, passando a recomendação atual como fallback
      const updatedRecommendationFromBackend = await giveRecommendationFeedback(
        recommendationId, 
        newLikedValue, 
        currentRecommendation // Passa a recomendação do estado local como fallback
      );

      if (updatedRecommendationFromBackend) {
        // --- Atualização Final do Estado com dados do Backend ---
        console.log(`Feedback salvo com sucesso para ID: ${recommendationId}. Novo estado:`, updatedRecommendationFromBackend.liked);
        const finalUpdatedList = recommendationsList.map(r => 
          r.id === recommendationId ? { ...r, liked: updatedRecommendationFromBackend.liked } : r
        );
        setRecommendations(prevState => ({
          ...prevState,
          [isPopular ? 'popular' : 'personalized']: finalUpdatedList
        }));
        // -------------------------------------------------------
        
        // Trigger para novas recomendações (lógica existente)
        const countPersonalizedWithFeedback = recommendations.personalized.filter(r => r.liked !== null).length;
        const totalPersonalized = recommendations.personalized.length;
        if ((totalPersonalized > 0 && countPersonalizedWithFeedback >= Math.max(3, Math.floor(totalPersonalized / 2)))) {
          // ... (lógica de notificação e recarga) ...
        }

      } else {
        // --- Reverter Estado em Caso de Falha no Backend --- 
        console.error(`Falha ao salvar feedback para ID: ${recommendationId}. Revertendo UI.`);
        setRecommendations(prevState => ({
          ...prevState,
          [isPopular ? 'popular' : 'personalized']: recommendationsList // Usa a lista original antes da atualização otimista
        }));
        setError('Falha ao salvar seu feedback. Tente novamente.');
        // --------------------------------------------------
      }

    } catch (feedbackError) {
      // --- Reverter Estado em Caso de Erro Inesperado --- 
      console.error(`Erro inesperado ao salvar feedback para ID: ${recommendationId}:`, feedbackError);
      setRecommendations(prevState => ({
        ...prevState,
        [isPopular ? 'popular' : 'personalized']: recommendationsList // Reverte para a lista original
      }));
      setError('Ocorreu um erro inesperado ao salvar seu feedback.');
       // ----------------------------------------------------
    }
    
    // Lógica para carregar mais recomendações se necessário (existente)
    const totalRecommendationsCount = recommendations.popular.length + recommendations.personalized.length;
    if (totalRecommendationsCount < 3) {
      // ... (lógica de recarga) ...
    }
  }, [recommendations, loadRecommendationsAndUserGames]); // Adicionar setError nas dependências se for função de estado
  
  // Converter para useCallback para evitar re-renderizações
  const handleToggleOwned = useCallback(async (gameId: string, owned: boolean) => {
    try {
      if (!user?.id) return;
      await toggleOwned(user.id, gameId, owned);
      
      // Atualizar o estado local
      setUserGames(prevGames => ({
        ...prevGames,
        [gameId]: {
          ...prevGames[gameId],
          owned,
          game_id: gameId,
          user_id: user.id
        }
      }));
    } catch (error) {
      console.error('Erro ao atualizar status de propriedade:', error);
    }
  }, [user?.id]);
  
  // Converter para useCallback para evitar re-renderizações
  const handleToggleWishlist = useCallback(async (gameId: string, wishlist: boolean) => {
    try {
      if (!user?.id) return;
      await toggleWishlist(user.id, gameId, wishlist);
      
      // Atualizar o estado local
      setUserGames(prevGames => ({
        ...prevGames,
        [gameId]: {
          ...prevGames[gameId],
          wishlist,
          game_id: gameId,
          user_id: user.id
        }
      }));
    } catch (error) {
      console.error('Erro ao atualizar status de wishlist:', error);
    }
  }, [user?.id]);

  // Adaptadores para o componente RecommendationCard que espera funções com uma assinatura diferente
  const handleAddToCollection = useCallback(async (gameId: string) => {
    const currentOwned = userGames[gameId]?.owned || false;
    await handleToggleOwned(gameId, !currentOwned);
  }, [userGames, handleToggleOwned]);

  const handleAddToWishlist = useCallback(async (gameId: string) => {
    const currentWishlist = userGames[gameId]?.wishlist || false;
    await handleToggleWishlist(gameId, !currentWishlist);
  }, [userGames, handleToggleWishlist]);

  // Renderizar tela de primeiro acesso para usuários sem recomendações
  const renderFirstTimeUser = () => (
    <div className="bg-white rounded-lg shadow-md p-5 md:p-8 dark:bg-gray-800 dark:border dark:border-gray-700">
      <div className="text-center py-6">
        <div className="mb-4 bg-indigo-100 rounded-full h-20 w-20 mx-auto flex items-center justify-center dark:bg-indigo-900/30">
          <Sparkles className="h-10 w-10 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 dark:text-gray-100">
          {loading ? 'Carregando Recomendações...' : 'Vamos encontrar jogos para você!'}
        </h2>
        
        {insufficientData && !loading ? (
          <>
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 my-4 rounded-md text-left dark:bg-orange-900/30 dark:border-orange-500">
              <p className="text-orange-800 text-sm font-semibold mb-2 dark:text-orange-300">
                Dados insuficientes para recomendações de qualidade
              </p>
              <p className="text-orange-700 text-sm mb-2 dark:text-orange-400">
                Para que possamos oferecer recomendações personalizadas de alta qualidade, você precisa fornecer mais informações sobre seus gostos em jogos.
              </p>
              <p className="text-orange-700 text-sm dark:text-orange-400">
                Por favor, realize pelo menos uma das seguintes ações:
              </p>
              <ul className="list-disc list-inside text-sm text-orange-700 mt-1 ml-2 dark:text-orange-400">
                <li>Adicione pelo menos 3 jogos à sua coleção (atual: {dataStats.totalGames})</li>
                <li>Avalie pelo menos 3 jogos (atual: {dataStats.ratedGames})</li>
                <li>Marque pelo menos 3 jogos como jogados (atual: {dataStats.playedGames})</li>
                <li>Adicione pelo menos 3 jogos à sua wishlist (atual: {dataStats.wishlistGames})</li>
              </ul>
              <div className="mt-3">
                <Link 
                  to="/search"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Ir para catálogo de jogos →
                </Link>
              </div>
            </div>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto text-sm md:text-base dark:text-gray-400">
              Baseado nos jogos da sua coleção, podemos recomendar novos títulos que você provavelmente vai gostar. Clique abaixo para gerar suas recomendações personalizadas.
            </p>
          </>
        ) : (
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto text-sm md:text-base dark:text-gray-400">
            {loading
              ? 'Aguarde enquanto preparamos suas recomendações personalizadas... Este processo pode levar alguns instantes, pois estamos analisando seu perfil para encontrar os melhores jogos.'
              : 'Baseado nos jogos da sua coleção, podemos recomendar novos títulos que você provavelmente vai gostar. Clique abaixo para gerar suas recomendações personalizadas.'}
          </p>
        )}
        
        {loading && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4 rounded-md dark:bg-yellow-900/30 dark:border-yellow-500">
            <p className="text-yellow-700 text-sm dark:text-yellow-300">
              <strong>Observação:</strong> O primeiro carregamento de recomendações pode demorar um pouco mais. Estamos constantemente melhorando nosso sistema para torná-lo mais rápido.
            </p>
          </div>
        )}
        <button
          onClick={handleGenerateRecommendations}
          disabled={generating || loading}
          className="w-full md:w-auto px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center mx-auto dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <RefreshCw className={`h-5 w-5 mr-2 ${generating ? 'animate-spin' : ''}`} />
          {generating ? 'Gerando Recomendações...' : 'Gerar Recomendações'}
        </button>
        {generating && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4 rounded-md dark:bg-yellow-900/30 dark:border-yellow-500">
            <p className="text-yellow-700 text-sm dark:text-yellow-300">
              <strong>Importante:</strong> A geração de recomendações pode levar alguns minutos, pois nosso sistema realiza análises complexas para encontrar os jogos mais adequados para você. Por favor, aguarde enquanto trabalhamos nisso.
            </p>
          </div>
        )}
      </div>
    </div>
  );
  
  // Renderizar seção de recomendações
  const renderRecommendationsSection = () => {
    const hasPopular = recommendations.popular.length > 0;
    const hasPersonalized = recommendations.personalized.length > 0;
    
    // Debug logs removidos para clareza
    
    if (!hasPopular && !hasPersonalized) {
      return (
        <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg dark:bg-gray-800 dark:border dark:border-gray-700">
          <Frown className="w-10 h-10 md:w-12 md:h-12 mx-auto text-gray-400 mb-4 dark:text-gray-500" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2 dark:text-gray-200">
            Nenhuma recomendação disponível
          </h3>
          <p className="text-gray-600 mb-4 text-sm md:text-base px-4 dark:text-gray-400">
            Não conseguimos encontrar recomendações para você neste momento. 
            Tente gerar novas recomendações ou adicione mais jogos à sua coleção.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 max-w-2xl mx-auto rounded-md dark:bg-yellow-900/30 dark:border-yellow-500">
            <p className="text-yellow-700 text-sm text-left dark:text-yellow-300">
              <strong>Por que isso acontece?</strong> Nosso sistema de recomendação precisa de dados suficientes para fazer sugestões relevantes. 
              O processo de geração pode demorar alguns minutos, pois envolve análises complexas dos seus gostos e preferências. 
              Estamos constantemente trabalhando para tornar este processo mais rápido e eficiente.
            </p>
          </div>
          <button
            onClick={handleGenerateRecommendations}
            disabled={generating}
            className="w-full mx-4 md:w-auto px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center md:mx-auto dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Gerando...' : 'Gerar Recomendações'}
          </button>
        </div>
      );
    }
    
    return (
      <div className="space-y-8 md:space-y-12">
        {/* Recomendações populares - Exibir sempre primeiro */}
        {hasPopular && (
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6 border-b pb-2 dark:text-gray-100 dark:border-gray-700">
              Jogos populares no momento
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {recommendations.popular.map((rec: Recommendation) => (
                <RecommendationCard
                  key={rec.id}
                  recommendation={rec}
                  isPopular={true}
                  userGame={userGames[rec.game_id]}
                  onFeedback={handleFeedback}
                  onAddToCollection={handleAddToCollection}
                  onToggleWishlist={handleAddToWishlist}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Aviso de recomendações personalizadas em processamento */}
        {onlyPopularRecommendations && (
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 my-6 rounded-md dark:bg-indigo-900/30 dark:border-indigo-500">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <Sparkles className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
              </div>
              <div className="ml-3 w-full">
                <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Recomendações personalizadas ainda não disponíveis</h3>
                <div className="mt-2 text-sm text-indigo-700 dark:text-indigo-400">
                  <p>
                    Notamos que você já tem {dataStats.totalGames} jogos na sua coleção, mas suas recomendações personalizadas ainda não estão prontas.
                  </p>
                  <p className="mt-1">
                    Nosso sistema está processando seus dados para criar recomendações personalizadas de alta qualidade. Este processo pode levar algum tempo, especialmente nas primeiras utilizações. Por enquanto, estamos mostrando jogos populares que podem te interessar.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <button
                      onClick={() => loadRecommendationsAndUserGames(false)}
                      disabled={generating || loading}
                      className="px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs rounded-md hover:bg-indigo-200 transition flex items-center dark:bg-indigo-700/50 dark:text-indigo-300 dark:hover:bg-indigo-700/70"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                      <span>{loading ? 'Atualizando...' : 'Atualizar recomendações'}</span>
                    </button>
                    
                    <button
                      onClick={() => loadRecommendationsAndUserGames(true)}
                      disabled={generating || loading}
                      className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition flex items-center justify-center dark:bg-indigo-500 dark:hover:bg-indigo-600"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      <span>{loading ? 'Processando...' : 'Forçar recomendações personalizadas'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Recomendações personalizadas */}
        {hasPersonalized && (
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6 border-b pb-2 dark:text-gray-100 dark:border-gray-700">
              Recomendados para você
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {recommendations.personalized.map((rec: Recommendation) => (
                <RecommendationCard
                  key={rec.id}
                  recommendation={rec}
                  isPopular={false}
                  userGame={userGames[rec.game_id]}
                  onFeedback={handleFeedback}
                  onAddToCollection={handleAddToCollection}
                  onToggleWishlist={handleAddToWishlist}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SEO 
        title="Recomendações"
        description="Descubra novos jogos de tabuleiro baseados em seus gostos e preferências."
      />
      <div className="container mx-auto px-4 py-4 md:py-8 max-w-6xl">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">Recomendações</h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to="/feedback"
              className="px-4 py-2 text-center bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-600 rounded-lg transition flex items-center justify-center dark:bg-gray-700 dark:text-indigo-300 dark:border-indigo-500 dark:hover:bg-gray-600"
            >
              <ThumbsUp className="h-5 w-5 mr-2" />
              <span className="whitespace-nowrap">Meus Feedbacks</span>
            </Link>
            {!firstTimeUser && (
              <button
                onClick={handleGenerateRecommendations}
                disabled={generating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <RefreshCw className={`h-5 w-5 mr-2 ${generating ? 'animate-spin' : ''}`} />
                <span className="whitespace-nowrap">{generating ? 'Gerando...' : 'Novas Recomendações'}</span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 text-sm md:text-base dark:bg-red-900/30 dark:text-red-400">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 text-sm md:text-base dark:bg-green-900/30 dark:text-green-400">
            {success}
          </div>
        )}

        {insufficientData && !firstTimeUser && !loading && (
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6 rounded-md dark:bg-orange-900/30 dark:border-orange-500">
            <p className="text-orange-800 text-sm font-semibold mb-2 dark:text-orange-300">
              Dados insuficientes para recomendações de qualidade
            </p>
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0 md:mr-4">
                <p className="text-orange-700 text-sm mb-2 dark:text-orange-400">
                  Para melhores recomendações, considere:
                </p>
                <ul className="list-disc list-inside text-sm text-orange-700 ml-2 dark:text-orange-400">
                  <li>Adicionar mais jogos à sua coleção (atual: {dataStats.totalGames})</li>
                  <li>Avaliar mais jogos (atual: {dataStats.ratedGames})</li>
                  <li>Marcar jogos como jogados (atual: {dataStats.playedGames})</li>
                  <li>Adicionar jogos à sua wishlist (atual: {dataStats.wishlistGames})</li>
                </ul>
              </div>
              <Link 
                to="/search"
                className="text-sm font-medium px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center self-start dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Explorar Catálogo de Jogos
              </Link>
            </div>
          </div>
        )}

        {generating && !loading && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-md dark:bg-yellow-900/30 dark:border-yellow-500">
            <p className="text-yellow-700 text-sm dark:text-yellow-300">
              <strong>Importante:</strong> A geração de novas recomendações pode levar alguns minutos. Nosso sistema está analisando seu perfil e histórico de jogos para encontrar as melhores sugestões personalizadas. Estamos constantemente trabalhando para melhorar a velocidade deste processo.
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 md:py-12">
            <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 text-sm md:text-base dark:text-gray-400">Carregando suas recomendações...</p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6 max-w-2xl mx-auto rounded-md dark:bg-yellow-900/30 dark:border-yellow-500">
              <p className="text-yellow-700 text-sm dark:text-yellow-300">
                <strong>Observação:</strong> O carregamento de recomendações pode demorar alguns minutos. Este processo utiliza algoritmos avançados que analisam seus jogos e preferências para oferecer sugestões personalizadas. Estamos trabalhando para tornar este processo mais rápido em atualizações futuras.
              </p>
            </div>
          </div>
        ) : firstTimeUser && (!recommendations.popular.length && !recommendations.personalized.length) ? (
          renderFirstTimeUser()
        ) : (
          renderRecommendationsSection()
        )}
      </div>
    </>
  );
};

export default RecommendationsPage;